import { assert } from "@tldraw/editor";
import {
  computeRadii,
  ingest,
  inputX,
  inputY,
  loadSrcFromPipeline,
  loadSrcPartition,
  pointCount,
  pointX,
  pointY,
  radii,
  srcCount,
  srcRadius,
  srcX,
  srcY
} from "./core.mjs";
import { finishPath, resetPath, toCenti, writeC, writeCPair, writeStr } from "./fmt.mjs";
import {
  buildTracks,
  trackLeftCount,
  trackLeftX,
  trackLeftY,
  trackRightCount,
  trackRightX,
  trackRightY
} from "./getStrokeOutlinePoints.mjs";
function svgInk(rawInputPoints, options = {}) {
  const { start = {}, end = {} } = options;
  const { cap: capStart = true } = start;
  const { cap: capEnd = true } = end;
  assert(!start.taper && !end.taper, "cap taper not supported here");
  assert(!start.easing && !end.easing, "cap easing not supported here");
  assert(capStart && capEnd, "cap must be true");
  ingest(rawInputPoints, options);
  computeRadii(options);
  resetPath();
  partitionAtElbows(options);
  return finishPath();
}
function partitionAtElbows(options) {
  const n = pointCount;
  if (n === 0) return;
  if (n <= 2) {
    loadSrcFromPipeline();
    renderPartition(options, false, 0, 0);
    return;
  }
  const ptX = pointX;
  const ptY = pointY;
  const rads = radii;
  let a = 0;
  let aElbow = false;
  let hasAnchor = false;
  let anchorX = 0;
  let anchorY = 0;
  let dx = ptX[1] - ptX[0];
  let dy = ptY[1] - ptY[0];
  let len = Math.sqrt(dx * dx + dy * dy);
  let prevVx = dx / len;
  let prevVy = dy / len;
  for (let i = 1; i < n - 1; i++) {
    dx = ptX[i + 1] - ptX[i];
    dy = ptY[i + 1] - ptY[i];
    len = Math.sqrt(dx * dx + dy * dy);
    const nextVx = dx / len;
    const nextVy = dy / len;
    const dpr = prevVx * nextVx + prevVy * nextVy;
    prevVx = nextVx;
    prevVy = nextVy;
    if (dpr < -0.8) {
      finishPartition(a, aElbow, i, true, false, hasAnchor, anchorX, anchorY, options);
      a = i;
      aElbow = true;
      hasAnchor = true;
      anchorX = ptX[i];
      anchorY = ptY[i];
      continue;
    }
    if (dpr > 0.7) {
      continue;
    }
    const pdx = ptX[i] - ptX[i - 1];
    const pdy = ptY[i] - ptY[i - 1];
    const ndx = ptX[i + 1] - ptX[i];
    const ndy = ptY[i + 1] - ptY[i];
    const meanRadius = (rads[i - 1] + rads[i] + rads[i + 1]) / 3;
    if ((pdx * pdx + pdy * pdy + ndx * ndx + ndy * ndy) / (meanRadius * meanRadius) < 1.5) {
      finishPartition(a, aElbow, i, false, true, hasAnchor, anchorX, anchorY, options);
      a = i;
      aElbow = false;
      hasAnchor = false;
      continue;
    }
  }
  finishPartition(a, aElbow, n - 1, false, false, hasAnchor, anchorX, anchorY, options);
}
function finishPartition(a, aElbow, b, bElbow, bDup, hasAnchor, anchorX, anchorY, options) {
  const ptX = pointX;
  const ptY = pointY;
  const rads = radii;
  const len = b - a + 1 + (bDup ? 1 : 0);
  let s = 0;
  let e = 0;
  const startX = aElbow ? inputX[a] : ptX[a];
  const startY = aElbow ? inputY[a] : ptY[a];
  const startRadius = rads[a];
  while (len - s > 2) {
    const i = a + 1 + s;
    const dx = startX - ptX[i];
    const dy = startY - ptY[i];
    if (dx * dx + dy * dy < ((startRadius + rads[i]) / 2 * 0.5) ** 2) {
      hasAnchor = true;
      anchorX = ptX[i];
      anchorY = ptY[i];
      s++;
    } else {
      break;
    }
  }
  const endX = bElbow ? inputX[b] : ptX[b];
  const endY = bElbow ? inputY[b] : ptY[b];
  const endRadius = rads[b];
  while (len - s - e > 2) {
    const i = bDup ? b - e : b - 1 - e;
    const dx = endX - ptX[i];
    const dy = endY - ptY[i];
    if (dx * dx + dy * dy < ((endRadius + rads[i]) / 2 * 0.5) ** 2) {
      e++;
    } else {
      break;
    }
  }
  const innerStart = a + 1 + s;
  const innerEnd = bDup ? b - e : b - 1 - e;
  loadSrcPartition(a, aElbow, innerStart, innerEnd, b, bElbow, bDup && e === 0);
  renderPartition(options, hasAnchor, anchorX, anchorY);
}
function writeCirclePath(cx, cy, r) {
  const ncx = toCenti(cx);
  const ncy = toCenti(cy);
  const nr = toCenti(r);
  writeStr("M ");
  writeC(ncx);
  writeStr(" ");
  writeC(ncy);
  writeStr(" m -");
  writeC(nr);
  writeStr(", 0 a ");
  writeC(nr);
  writeStr(",");
  writeC(nr);
  writeStr(" 0 1,1 ");
  writeC(nr * 2);
  writeStr(",0 a ");
  writeC(nr);
  writeStr(",");
  writeC(nr);
  writeStr(" 0 1,1 -");
  writeC(nr * 2);
  writeStr(",0");
}
function writeCapArc(nr, dx, dy) {
  writeStr("a");
  writeC(nr);
  writeStr(",");
  writeC(nr);
  writeStr(" 0 0 1 ");
  writeCPair(dx, dy);
}
function renderPartition(options, hasAnchor, anchorX, anchorY) {
  const n = srcCount;
  if (n === 0) return;
  if (n === 1) {
    writeCirclePath(srcX[0], srcY[0], srcRadius[0]);
    return;
  }
  buildTracks(options, hasAnchor, anchorX, anchorY);
  const lxs = trackLeftX;
  const lys = trackLeftY;
  const rxs = trackRightX;
  const rys = trackRightY;
  let cx = toCenti(lxs[0]);
  let cy = toCenti(lys[0]);
  writeStr("M");
  writeCPair(cx, cy);
  writeStr("t");
  let prevX = lxs[0];
  let prevY = lys[0];
  for (let i = 1; i < trackLeftCount; i++) {
    const ptX = lxs[i];
    const ptY = lys[i];
    const mx = Math.round((prevX + ptX) * 50);
    const my = Math.round((prevY + ptY) * 50);
    writeCPair(mx - cx, my - cy);
    cx = mx;
    cy = my;
    prevX = ptX;
    prevY = ptY;
  }
  {
    const pointX2 = srcX[n - 1];
    const pointY2 = srcY[n - 1];
    const radius = srcRadius[n - 1];
    const vdx = srcX[n - 2] - pointX2;
    const vdy = srcY[n - 2] - pointY2;
    const vlen = Math.sqrt(vdx * vdx + vdy * vdy);
    const dx = -vdy / vlen * radius;
    const dy = vdx / vlen * radius;
    const asx = toCenti(pointX2 + dx);
    const asy = toCenti(pointY2 + dy);
    const aex = toCenti(pointX2 - dx);
    const aey = toCenti(pointY2 - dy);
    writeCPair(asx - cx, asy - cy);
    writeCapArc(toCenti(radius), aex - asx, aey - asy);
    writeStr("t");
    cx = aex;
    cy = aey;
  }
  prevX = rxs[trackRightCount - 1];
  prevY = rys[trackRightCount - 1];
  for (let i = trackRightCount - 2; i >= 0; i--) {
    const ptX = rxs[i];
    const ptY = rys[i];
    const mx = Math.round((prevX + ptX) * 50);
    const my = Math.round((prevY + ptY) * 50);
    writeCPair(mx - cx, my - cy);
    cx = mx;
    cy = my;
    prevX = ptX;
    prevY = ptY;
  }
  {
    const pointX2 = srcX[0];
    const pointY2 = srcY[0];
    const radius = srcRadius[0];
    const vdx = pointX2 - srcX[1];
    const vdy = pointY2 - srcY[1];
    const vlen = Math.sqrt(vdx * vdx + vdy * vdy);
    const dx = vdy / vlen * radius;
    const dy = -vdx / vlen * radius;
    const asx = toCenti(pointX2 + dx);
    const asy = toCenti(pointY2 + dy);
    const aex = toCenti(pointX2 - dx);
    const aey = toCenti(pointY2 - dy);
    writeCPair(asx - cx, asy - cy);
    writeCapArc(toCenti(radius), aex - asx, aey - asy);
    writeStr("Z");
  }
}
export {
  svgInk
};
//# sourceMappingURL=svgInk.mjs.map
